import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";

import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Checkbox from "@mui/joy/Checkbox";
import Link from "@mui/joy/Link";
import Divider from "@mui/joy/Divider";
import CircularProgress from "@mui/joy/CircularProgress";
import Chip from "@mui/joy/Chip";

import DeleteRoundedIcon      from "@mui/icons-material/DeleteRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import CheckCircleRoundedIcon  from "@mui/icons-material/CheckCircleRounded";
import ArrowBackRoundedIcon    from "@mui/icons-material/ArrowBackRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StorefrontRoundedIcon   from "@mui/icons-material/StorefrontRounded";
import PersonRoundedIcon       from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon        from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon        from "@mui/icons-material/EmailRounded";
import CakeRoundedIcon         from "@mui/icons-material/CakeRounded";
import LocationOnRoundedIcon   from "@mui/icons-material/LocationOnRounded";
import SearchRoundedIcon       from "@mui/icons-material/SearchRounded";
import TaskAltRoundedIcon      from "@mui/icons-material/TaskAltRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import DiscountRoundedIcon     from "@mui/icons-material/DiscountRounded";
import PercentRoundedIcon      from "@mui/icons-material/PercentRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { IMaskInput } from 'react-imask';

import { API_URL, bookApi, DEMO_MODE } from "../../api.js";
import { MiniCoverPreview } from "./ModalMyBooks.jsx";
import { getBooksProblems } from "../../hooks/checkBooksReady.js";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Option, Select } from "@mui/joy";
import { LayoutContext } from "../../context.jsx";
import { sanitizeName } from "../../sanitize.js";
import { disableFitImageMode } from "../../fitImage";
import { disableConverterMode } from "../../heicConverter";
import { confirmVpnModal } from "../confirmVpnModal";
import { auditFetch } from "../../editorAudit";

// ─── Константы ────────────────────────────────────────────────────────────────

const DELIVERY_TYPES = [
  // {
  //   id: "pickup",
  //   label: "Самовывоз",
  //   desc: "Забрать из нашего офиса",
  //   icon: <StorefrontRoundedIcon sx={{ fontSize: 22 }} />,
  //   free: true,
  // },
  {
    id: "cdek",
    label: "СДЭК",
    desc: "Доставка до пункта выдачи или курьером",
    icon: <LocalShippingRoundedIcon sx={{ fontSize: 22 }} />,
    free: false,
  },
];


// ─── Анимация слайда ──────────────────────────────────────────────────────────

const slideVariants = {
  enterFromRight: { x: "60%", opacity: 0 },
  enterFromLeft:  { x: "-60%", opacity: 0 },
  center:         { x: 0, opacity: 1 },
  exitToLeft:     { x: "-60%", opacity: 0 },
  exitToRight:    { x: "60%", opacity: 0 },
};

const MONTHS = [
  "Январь","Февраль","Март","Апрель","Май","Июнь",
  "Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"
];
const YEARS = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);



// Компонент маски для телефона
const PhoneMaskInput = React.forwardRef(function PhoneMaskInput(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="+7 (000) 000-00-00"
      definitions={{ '0': /[0-9]/ }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

// ─── Карточка товара в корзине ────────────────────────────────────────────────

function CartItemCard({ item, onRemove }) {
  // Если есть полные данные книги — показываем MiniCoverPreview
  const hasBookData = !!item.bookData;

  return (
    <Box sx={{
      display: "flex", gap: 2, alignItems: "center",
      p: 1.5, borderRadius: "14px",
      border: "1px solid", borderColor: "divider",
      bgcolor: "background.level1",
    }}>
      {/* Превью обложки */}
      {hasBookData ? (
        <MiniCoverPreview book={item.bookData} width={90} height={72} />
      ) : (
        <Box sx={{
          width: 56, height: 72, borderRadius: "8px",
          bgcolor: "#e8e8ee", flexShrink: 0, overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}>
          {item.coverUrl && (
            <Box component="img" src={item.coverUrl}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="title-sm" noWrap fontWeight={700}>
          {item.title || "Фотокнига"}
        </Typography>
        <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
          {item.pagesCount} {item.pagesCount === 1 ? "страница" : item.pagesCount < 5 ? "страницы" : "страниц"} · {item.size}
        </Typography>
        {item.price != null && (
          <Typography level="body-sm" sx={{ color: "primary.600", fontWeight: 700, mt: 0.5 }}>
            {Math.round(parseFloat(item.price) || 0).toLocaleString("ru-RU")} ₽
          </Typography>
        )}
      </Box>

      <IconButton
        size="sm" variant="soft" color="danger"
        onClick={() => {
          if (window.confirm("Удалить книгу из корзины?")) onRemove();
        }}
        sx={{ borderRadius: "8px", flexShrink: 0 }}
      >
        <DeleteRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}


// ─── Поле формы ───────────────────────────────────────────────────────────────

function FormField({ label, icon, required, error, ...inputProps }) {
  return (
    <Box>
      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600, color: error ? "danger.500" : "text.secondary" }}>
        {label}{required && <Box component="span" sx={{ color: "danger.400", ml: 0.25 }}>*</Box>}
      </Typography>
      <Input
        // startDecorator={icon}
        size="sm"
        error={!!error}
        sx={{ 
          borderRadius: 0,
          px: 2, py: 2,
          fontSize: 15
        }}
        {...inputProps}
      />
      {error && (
        <Typography level="body-xs" sx={{ color: "danger.500", mt: 0.25 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}


// ─── Карточка типа доставки ───────────────────────────────────────────────────

function DeliveryCard({ type, selected, onClick, deliveryPrice, loadingPrice }) {
  return (
    <Box
      onClick={() => onClick(type.id)}
      sx={{
        p: 1.5,
        borderRadius: "12px",
        border: "2px solid",
        borderColor: selected
          ? "primary.400"
          : "color-mix(in srgb, var(--joy-palette-neutral-300) 60%, transparent)",
        bgcolor: selected
          ? "color-mix(in srgb, var(--joy-palette-primary-500) 6%, var(--joy-palette-background-surface))"
          : "background.surface",
        cursor: "pointer",
        transition: "all 0.16s",
        display: "flex", alignItems: "center", gap: 1.5,
        boxShadow: selected
          ? "0 0 0 3px color-mix(in srgb, var(--joy-palette-primary-400) 18%, transparent)"
          : "none",
        "&:hover": {
          borderColor: "color-mix(in srgb, var(--joy-palette-primary-400) 55%, transparent)",
        },
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Box sx={{
        color: selected ? "primary.500" : "neutral.400",
        transition: "color 0.16s",
      }}>
        {type.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="body-sm" fontWeight={selected ? 700 : 500}>
          {type.label}
        </Typography>
        <Typography level="body-xs" color="neutral">{type.desc}</Typography>
      </Box>

      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        {type.free ? (
          <Chip size="sm" color="success" variant="soft" sx={{ fontSize: "11px", fontWeight: 700 }}>
            Бесплатно
          </Chip>
        ) : selected ? (
          loadingPrice ? (
            <CircularProgress size="sm" sx={{ "--CircularProgress-size": "18px" }} />
          ) : deliveryPrice != null ? (
            <Typography level="body-sm" sx={{ color: "primary.600", fontWeight: 700 }}>
              {deliveryPrice === 0 ? "Бесплатно" : `~${deliveryPrice.toLocaleString("ru-RU")} ₽`}
            </Typography>
          ) : (
            <Typography level="body-xs" color="neutral">введите адрес</Typography>
          )
        ) : (
          <Typography level="body-xs" color="neutral">расчёт при выборе</Typography>
        )}
      </Box>
    </Box>
  );
}


// ─── Успешный экран ───────────────────────────────────────────────────────────

function SuccessScreen({ onClose, orderNumber }) {
  return (
    <Stack alignItems="center" justifyContent="center" gap={2.5} sx={{ py: 6, px: 3 }}>
      {/* Анимированная иконка */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 22, delay: 0.1 }}
      >
        <Box sx={{
          width: 80, height: 80,
          borderRadius: "50%",
          bgcolor: "color-mix(in srgb, var(--joy-palette-success-400) 15%, transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <TaskAltRoundedIcon sx={{ fontSize: 44, color: "success.500" }} />
        </Box>
      </motion.div>

      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.25, duration: 0.3 }}
        style={{ textAlign: "center" }}
      >
        <Typography level="title-lg" fontWeight={800} sx={{ mb: 0.75 }}>
          Благодарим вас за доверие и заказ!
        </Typography>
        <Stack spacing={1.25} sx={{ mt: 2, maxWidth: 360, mx: "auto", textAlign: "left" }}>
          <Typography level="body-sm" color="neutral">
            Вам на указанную при оформлении заказа почту, в течение 20 минут,
            должно прийти уведомление с номером заказа.
          </Typography>
          <Typography level="body-sm" color="neutral">
            На нашем сайте вы можете ввести номер заказа и узнать стадию вашего
            заказа в разделе «Статус заказа».
          </Typography>
          <Typography level="body-sm" color="neutral">
            Фотокнигу изготавливаем в течение 7 рабочих дней и передаём в СДЭК.
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, fontWeight: 600 }}>
            С большим уважением, команда Magic Board!
          </Typography>
        </Stack>
      </motion.div>

      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.3 }}
        style={{ width: "100%" }}
      >
        <Button
          fullWidth size="lg" variant="solid" color="success"
          onClick={onClose}
          sx={{ borderRadius: "12px", fontWeight: 700 }}
        >
          Отлично, закрыть
        </Button>
      </motion.div>
    </Stack>
  );
}




// Фикс иконок Leaflet (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const selectedIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function MapFlyTo({ pvzList }) {
  const map = useMap();
  useEffect(() => {
    if (pvzList.length > 0) {
      map.setView([pvzList[0].lat, pvzList[0].lng], 12);
    }
  }, [pvzList]);
  return null;
}

function SdekMap({ pvzList, selectedCode, onSelect }) {
  const center = pvzList.length > 0 ? [pvzList[0].lat, pvzList[0].lng] : [55.75, 37.62];
  return (
    <Box sx={{ borderRadius: "10px", overflow: "hidden", border: "1px solid", borderColor: "divider", height: 260 }}>
      <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapFlyTo pvzList={pvzList} />
        {pvzList.map(pvz => (
          <Marker
            key={pvz.code}
            position={[pvz.lat, pvz.lng]}
            icon={pvz.code === selectedCode ? selectedIcon : new L.Icon.Default()}
            eventHandlers={{ click: () => onSelect(pvz) }}
          >
            <Popup>
              <strong>{pvz.name}</strong><br />
              {pvz.address_full || pvz.address}<br />
              {pvz.work_time && <span style={{ fontSize: 11 }}>{pvz.work_time}</span>}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Box>
  );
}



// Стабильный ключ идемпотентности: один и тот же для одного и того же чек-аута
// (одинаковый состав корзины + телефон). Повтор отправки — даже после возврата
// с оплаты — даёт тот же ключ, и сервер возвращает уже созданный заказ, а не
// плодит дубль. Изменился состав корзины → ключ меняется → это новый заказ.
function buildIdempotencyKey(items, phone) {
  const sig = (items || [])
    .map(i => i.bookId)
    .filter(Boolean)
    .sort()
    .join(",");
  const raw = `${(phone || "").replace(/\D/g, "")}|${sig}`;
  // FNV-1a с двумя сидами → 16 hex-символов (~64 бита), коллизии практически исключены
  const fnv = (seed) => {
    let h = seed >>> 0;
    for (let i = 0; i < raw.length; i++) {
      h ^= raw.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
  };
  return `cart-${fnv(0x811c9dc5)}${fnv(0x9e3779b1)}`;
}


// ─── Главный компонент ────────────────────────────────────────────────────────

export default function ModalCart({
  open, onClose, cartItems, onRemoveItem, onOrderSuccess, onRefreshItems
}) {
  const { setCartItems, setSelectedBookIds } = useContext(LayoutContext);
  
  const submittingRef = useRef(false);

  // Шаги: "cart" → "checkout" → "success"
  const [step, setStep]       = useState("checkout");
  const [direction, setDir]   = useState(1); // 1 = вперёд, -1 = назад

  // Форма
  const [form, setForm] = useState({
    name: "", phone: "", email: "", birthDate: "",
    deliveryType: "cdek", cityCode: null,
    city: "", cityCode: "", pvzCode: "", pvzAddress: "", comment: "",
  });
  const [errors, setErrors]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [agreed, setAgreed]             = useState(false); // обработка перс. данных
  const [agreedThirdParty, setAgreedThirdParty] = useState(false); // передача третьим лицам
  const [agreedMarketing, setAgreedMarketing]   = useState(false); // рекламная рассылка

  const [birthDay,   setBirthDay]   = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear,  setBirthYear]  = useState("");

  // Единое поле промокод/сертификат
  const [codeInput, setCodeInput] = useState("");
  const [codeStatus, setCodeStatus] = useState(null); // null | "checking" | "valid" | "invalid" | "mismatch"
  const [codeError, setCodeError] = useState("");
  const [codeType, setCodeType] = useState(null);   // "promo" | "gift" | null
  const [codeData, setCodeData] = useState(null);   // promo или gift данные
  const [giftMismatch, setGiftMismatch] = useState(null);

  // Автоскидка
  const [autoDiscount, setAutoDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [promoFreeDelivery, setPromoFreeDelivery] = useState(false);

  // Скидка по телефону (новый эндпоинт)
  const [phoneDiscount, setPhoneDiscount] = useState(null); // объект скидки
  const [phoneDiscountLoading, setPhoneDiscountLoading] = useState(false);
  const [phoneDiscountApplied, setPhoneDiscountApplied] = useState(false); // true если применена (нет промокода)

  // СДЭК
  const [deliveryPrice, setDeliveryPrice]   = useState(null);
  const [loadingPrice, setLoadingPrice]     = useState(false);
  const [deliveryError, setDeliveryError]   = useState(null);
  const sdekTimerRef = useRef(null);
  const pvzSearchTimerRef = useRef(null);
  const scrollBoxRef = useRef(null);

  const [pvzSearchQuery, setPvzSearchQuery] = useState("");
  const [pvzFiltered, setPvzFiltered] = useState([]);

  const [pvzList, setPvzList] = useState([]);
  const [pvzLoading, setPvzLoading] = useState(false);

  const totalItems = cartItems.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
  const giftApplied = codeStatus === "valid" && codeType === "gift" && !!codeData;
  const giftValue = giftApplied ? Number(codeData.gift_value) : 0;
  const giftCartValid = !giftApplied || (
    cartItems.length === 1 && Number(cartItems[0]?.quantity ?? 1) === 1 &&
    Number.isFinite(giftValue) && giftValue > 0 &&
    Number(cartItems[0]?.price) >= giftValue
  );
  const shippingCredit = giftApplied ? Math.max(0, Number(codeData.delivery_value) || 0) : 0;
  const quotedDelivery = Number(deliveryPrice) || 0;
  const giftDeliveryUsed = form.deliveryType === "cdek"
    ? Math.min(quotedDelivery, shippingCredit) : 0;
  const totalDelivery = form.deliveryType === "pickup" || promoFreeDelivery
    ? 0 : Math.max(0, quotedDelivery - giftDeliveryUsed);
  const giftFreeDelivery = giftApplied && form.deliveryType === "cdek" &&
    deliveryPrice != null && totalDelivery === 0;
  const totalFinal = totalItems + totalDelivery;
  const deliveryNeedsPrice = form.deliveryType === "cdek" && !promoFreeDelivery &&
    (loadingPrice || !(Number(deliveryPrice) > 0));
  const itemsNeedPrice = cartItems.some(i => !(Number(i.price) > 0));
  const giftDiscount = giftApplied && giftCartValid ? giftValue : 0;

  // Промокод применён (процент ИЛИ фиксированная сумма)
  const promoApplied = (codeStatus === "valid" && codeType === "promo" && codeData);
  const promoIsFixed = promoApplied && codeData.promo?.discount_type === "fixed";
  const promoPercent = (promoApplied && !promoIsFixed)
    ? parseFloat(codeData.promo?.percent || 0)
    : 0;
  const promoFixedAmount = promoIsFixed
    ? parseFloat(codeData.promo?.amount || 0)
    : 0;

  // Если промокод применён — скидка по телефону/авто НЕ применяется
  const phonePercent = (!giftApplied && !promoApplied && phoneDiscount)
    ? parseFloat(phoneDiscount.percent || 0)
    : 0;
  const autoPercent = (!giftApplied && !promoApplied && !phonePercent && autoDiscount)
    ? parseFloat(autoDiscount.percent || 0)
    : 0;

  const appliedDiscountPercent = promoPercent || phonePercent || autoPercent;
  // База для скидки — товары минус сертификат (не уходим в минус)
  const discountBase = Math.max(0, totalItems - giftDiscount);
  // Сумма скидки: фикс ограничиваем стоимостью товаров, иначе считаем процент
  const discountAmount = promoIsFixed
    ? Math.min(Math.round(promoFixedAmount), discountBase)
    : Math.round(discountBase * appliedDiscountPercent / 100);

  // Флаг: скидка по телефону сейчас применяется
  const isPhoneDiscountActive = phonePercent > 0 && !promoApplied;

  // Сброс при закрытии
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep("checkout");
        setDir(1);
        setPromoFreeDelivery(false);
        setForm({ name: "", phone: "", email: "", birthDate: "", deliveryType: "cdek", city: "", cityCode: "", pvzCode: "", pvzAddress: "", comment: "" });
        setErrors({});
        setDeliveryPrice(null);
        setDeliveryError(null);
        setGiftMismatch(null);

        setAutoDiscount(null);
        setPhoneDiscount(null);
        setPhoneDiscountLoading(false);
        setPhoneDiscountApplied(false);
      }, 300);
    }
  }, [open]);

  const handleBirthChange = (day, month, year) => {
    if (day && month && year) {
      const mm = String(month).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      upd("birthDate", `${year}-${mm}-${dd}`);
    } else {
      upd("birthDate", "");
    }
  };

  const deliveryRequestRef = useRef(0);
  const calcSdekPrice = async (cityCode) => {
    const requestId = ++deliveryRequestRef.current;
    setDeliveryPrice(null);
    setDeliveryError(null);
    if (!cityCode) { setLoadingPrice(false); return; }
    setLoadingPrice(true);
    try {
      const res = await auditFetch(`${API_URL}/sdek/tariff/`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_code: cityCode, tariff_code: 136 }),
      });
      if (!res.ok) throw new Error("Не удалось рассчитать доставку");
      const data = await res.json();
      const sum = data.delivery_sum ?? data.total_sum;
      if (!(Number(sum) > 0)) throw new Error("СДЭК не вернул стоимость");
      if (requestId === deliveryRequestRef.current) setDeliveryPrice(Number(sum));
    } catch (err) {
      if (requestId === deliveryRequestRef.current) setDeliveryError("Не удалось рассчитать доставку. Нажмите «Повторить».");
    } finally {
      if (requestId === deliveryRequestRef.current) setLoadingPrice(false);
    }
  };
  // Смена формата/страниц/состава корзины требует нового тарифа.
  const deliveryCartKey = cartItems.map(i =>
    `${i.bookId}:${i.bookFormatId}:${i.pagesCount}:${i.quantity ?? 1}`
  ).join("|");
  useEffect(() => {
    if (open && form.deliveryType === "cdek" && form.cityCode) calcSdekPrice(form.cityCode);
    return () => { deliveryRequestRef.current += 1; };
  }, [open, form.deliveryType, form.cityCode, deliveryCartKey]);

  // ── Поиск города СДЭК с debounce ──────────────────────────────────────────
  const [cityResults, setCityResults] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const handleCityInput = (val) => {
    deliveryRequestRef.current += 1;
    setLoadingPrice(false);
    setForm(f => ({ ...f, city: val, cityCode: "", pvzCode: "", pvzAddress: "" }));
    setDeliveryPrice(null);
    clearTimeout(sdekTimerRef.current);
    if (val.length < 2) { setCityResults([]); setShowCityDropdown(false); return; }
    sdekTimerRef.current = setTimeout(async () => {
      setCityLoading(true);
      try {
        const baseUrl = API_URL;
        const res = await fetch(`${baseUrl}/sdek/cities/?q=${encodeURIComponent(val)}`);
        const data = await res.json();
        setCityResults(data.slice(0, 6));
        setShowCityDropdown(true);
      } catch {
        setCityResults([]);
      } finally {
        setCityLoading(false);
      }
    }, 450);
  };

  const selectCity = async (city) => {
    setForm(f => ({ ...f, city: city.city, cityCode: city.code, pvzCode: "", pvzAddress: "" }));
    setCityResults([]);
    setShowCityDropdown(false);
    setPvzSearchQuery("");
    setPvzFiltered([]);

    setPvzLoading(true);
    setPvzList([]);
    try {
      const res = await fetch(`${API_URL}/sdek/pickup-points/?city_code=${city.code}`);
      const data = await res.json();
      setPvzList(data);
      setPvzFiltered(data);
      // Скроллим вниз после появления карты
      setTimeout(() => {
        scrollBoxRef.current?.scrollTo({ top: scrollBoxRef.current.scrollHeight, behavior: "smooth" });
      }, 300);
    } catch {
      setPvzList([]);
      setPvzFiltered([]);
    } finally {
      setPvzLoading(false);
      // Пересчёт запускает эффект по form.cityCode.
    }
  };

  const handlePvzSearch = (val) => {
    setPvzSearchQuery(val);
    clearTimeout(pvzSearchTimerRef.current);
    pvzSearchTimerRef.current = setTimeout(() => {
      if (!val.trim()) {
        setPvzFiltered(pvzList);
        return;
      }
      const q = val.toLowerCase();
      setPvzFiltered(pvzList.filter(p =>
        (p.address_full || p.address || "").toLowerCase().includes(q) ||
        (p.name || "").toLowerCase().includes(q) ||
        (p.work_time || "").toLowerCase().includes(q)
      ));
    }, 250);
  };


  // ── Автопроверка скидок при открытии ──────────────────────────────────────
  useEffect(() => {
    if (!open || cartItems.length === 0) return;
    
    const refreshBookData = async () => {
      const updatedItems = await Promise.all(
        cartItems.map(async (item) => {
          if (!item.bookId) return item;
          try {
            const freshBook = await bookApi.getBook(item.bookId);
            return {
              ...item,
              bookData: freshBook,
              price: freshBook.price ?? item.price,
              pagesCount: freshBook.pages_count ?? item.pagesCount,
            };
          } catch {
            return item;
          }
        })
      );
      // Обновляем только если данные изменились
      const changed = updatedItems.some((item, i) => item !== cartItems[i]);
      if (changed) {
        onRefreshItems?.(updatedItems);
      }
    };
    
    refreshBookData();
  }, [open]);

  // ── Проверка скидки по телефону (когда номер полностью введён) ──
  useEffect(() => {
    // Маска: +7 (999) 999-99-99 = 18 символов
    const isPhoneComplete = form.phone && form.phone.replace(/[^\d]/g, "").length >= 11;
    if (!open || !isPhoneComplete) {
      setPhoneDiscount(null);
      setPhoneDiscountApplied(false);
      return;
    }

    const timer = setTimeout(async () => {
      setPhoneDiscountLoading(true);
      try {
        const formatIds = cartItems.map(i => i.bookFormatId).filter(Boolean);
        const result = await bookApi.getDiscountByPhone({
          phone: form.phone,
          bookFormatIds: formatIds,
          itemsCount: cartItems.length,
        });

        // Скидка
        if (result.best_discount) {
          setPhoneDiscount(result.best_discount);
          setPhoneDiscountApplied(true);
        } else {
          setPhoneDiscount(null);
          setPhoneDiscountApplied(false);
        }

        // Дата рождения — заполняем если пришла и поля пустые
        if (result.birth_date) {
          const [y, m, d] = result.birth_date.split("-");
          const day = parseInt(d, 10);
          const month = parseInt(m, 10);
          const year = parseInt(y, 10);
          // Заполняем только если пользователь ещё не вводил
          if (!birthDay && !birthMonth && !birthYear) {
            setBirthDay(day);
            setBirthMonth(month);
            setBirthYear(year);
            handleBirthChange(day, month, year);
          }
        }
      } catch (err) {
        console.warn("Ошибка проверки скидки по телефону:", err);
        setPhoneDiscount(null);
        setPhoneDiscountApplied(false);
      } finally {
        setPhoneDiscountLoading(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [form.phone, open]);


  // ── Валидация ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!agreed)          e.agreed          = "Необходимо дать согласие на обработку персональных данных";
    if (!agreedThirdParty) e.agreedThirdParty = "Необходимо дать согласие на передачу данных третьим лицам";
    if (!agreedMarketing) e.agreedMarketing = "Необходимо дать согласие на рекламную и информационную рассылку";

    if (!form.last_name?.trim())   e.last_name = "Введите фамилию";
    if (!giftCartValid) e.price = "Сертификат требует одну книгу стоимостью не ниже номинала";
    if (!form.name?.trim())   e.name  = "Введите имя";
    if (!form.phone?.trim())  e.phone = "Введите телефон";

    if (form.phone && !/^[\d\s\+\-\(\)]{7,}$/.test(form.phone)) e.phone = "Некорректный номер";
    const emailTrimmed = (form.email || "").trim();
    if (!emailTrimmed) {
      e.email = "Введите email — на него придёт подтверждение заказа";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      e.email = "Некорректный email";
    }
    if (form.deliveryType === "cdek") {
      if (!form.city?.trim())    e.city    = "Введите город";
      if (!form.cityCode)       e.city    = "Выберите город из списка";
      if (!form.pvzCode)        e.pvz     = "Выберите пункт выдачи";
      if (deliveryNeedsPrice)   e.delivery = "Стоимость доставки не рассчитана. Обновите расчёт.";
    }
    if (codeInput?.trim()) {
      if (codeStatus === "mismatch") {
        e.codeInput = "Книга не соответствует условиям сертификата";
      } else if (codeStatus !== "valid") {
        e.codeInput = "Проверьте код или очистите поле";
      }
    }
    if (itemsNeedPrice) {
      e.price = "Стоимость книги не рассчитана. Обновите страницу корзины и попробуйте снова.";
    }
    return e;
  };

  // ── Отправка заказа ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    // Защёлка от двойного клика / StrictMode / повторного onClick
    if (submittingRef.current) return;
    if (submitting) return;

    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    // Предупреждение про VPN — показываем ДО создания заказа.
    const vpnConfirmed = await confirmVpnModal();
    if (!vpnConfirmed) return;

    submittingRef.current = true;
    setSubmitting(true);
    try {
      // режим сжатия и конвертера действует только до оформления — гасим перед заказом
      disableFitImageMode();
      disableConverterMode();
      const orderData = await bookApi.createOrder({
        clientName:      form.name,
        clientLastName:  form.last_name,
        clientPhone:     form.phone,
        clientEmail: (form.email || "").trim(),
        clientBirthDate: form.birthDate || undefined,
        deliveryType:    form.deliveryType,
        deliveryAddress: form.deliveryType === "cdek" ? form.pvzAddress : "",
        deliveryCity: form.city,
        deliveryCityCode: form.cityCode ? parseInt(form.cityCode) : undefined,
        deliveryPvzCode: form.pvzCode,
        deliveryPvzAddress: form.pvzAddress,
        deliveryComment: form.comment,
        deliveryPrice: promoFreeDelivery ? 0 : (deliveryPrice ?? 0),
        giftToken: (codeStatus === "valid" && codeType === "gift") ? codeInput.trim() : undefined,
        promoCode: (codeStatus === "valid" && codeType === "promo") ? codeInput.trim() : undefined,
        items: cartItems.map(i => ({
          bookId:   i.bookId,
          quantity: i.quantity ?? 1,
          price:    i.price ?? 0,
        })),
        consentPersonalData: agreed,
        consentThirdParty: agreedThirdParty,
        consentMarketing: agreedMarketing,
        // ключ идемпотентности (детерминированный от состава корзины + телефона)
        idempotencyKey: buildIdempotencyKey(cartItems, form.phone),
      });

      // Сервер поймал дубль (повторное оформление того же заказа) — это НЕ новый заказ.
      if (orderData.duplicate) {
        if (orderData.payment_url && !DEMO_MODE && orderData.status === "new") {
          // Заказ уже существует и не оплачен — ведём на оплату существующего.
          const ok = window.confirm(
            `Этот заказ уже был создан ранее (№${orderData.order_number || "—"}). ` +
            `Перейти к его оплате?`
          );
          if (ok) { window.location.href = orderData.payment_url; }
          return;
        }
        // Уже оплачен / в работе — просто сообщаем, нового заказа не создаём.
        alert(
          `Этот заказ уже оформлен ранее (№${orderData.order_number || "—"}, статус: ${orderData.status}). ` +
          `Повторное оформление не требуется.`
        );
        setStep("success");
        setCartItems([]);
        setSelectedBookIds([]);
        onOrderSuccess?.(cartItems.map(i => i.bookId).filter(Boolean));
        return;
      }

      if (orderData.payment_url && !DEMO_MODE) {
        const orderedBookIds = cartItems.map(i => i.bookId).filter(Boolean);
        const savedIds = JSON.parse(localStorage.getItem("photobook_ids") || "[]");
        const remaining = savedIds.filter(id => !orderedBookIds.includes(id));
        localStorage.setItem("photobook_ids", JSON.stringify(remaining));
        localStorage.setItem("pending_order_id", orderData.id);
        localStorage.setItem("pending_order_number", orderData.order_number);

        window.location.href = orderData.payment_url;
        return;
      }

      const orderedBookIds = cartItems.map(i => i.bookId).filter(Boolean);
      const savedIds = JSON.parse(localStorage.getItem("photobook_ids") || "[]");
      const remaining = savedIds.filter(id => !orderedBookIds.includes(id));
      localStorage.setItem("photobook_ids", JSON.stringify(remaining));

      setDir(1);
      setStep("success");
      setCartItems([]);
      setSelectedBookIds([]);
      onOrderSuccess?.(orderedBookIds);
    } catch (err) {
      if (err.status === 429) {
        // Параллельный запрос с тем же ключом ещё выполняется — это НЕ дубль.
        alert("Заказ уже обрабатывается. Подождите несколько секунд и не отправляйте повторно.");
      } else if (err.status === 409 && err.data?.code === "books_in_active_order") {
        // Одна или несколько книг уже входят в другой активный заказ.
        const conflicts = err.data.conflicts || [];
        // Неоплаченный «new»-заказ можно просто до-оплатить, а не создавать новый.
        const unpaid = conflicts.filter(c => c.order_status === "new" && !c.order_paid);
        const others = conflicts.filter(c => !(c.order_status === "new" && !c.order_paid));

        if (unpaid.length === 1 && others.length === 0) {
          const c = unpaid[0];
          const ok = window.confirm(
            `«${c.title}» уже в неоплаченном заказе №${c.order_number}. Перейти к его оплате?`
          );
          if (ok) {
            try {
              const { payment_url } = await bookApi.payOrder(c.order_id);
              if (payment_url) {
                try {
                  localStorage.setItem("pending_order_id", c.order_id);
                  localStorage.setItem("pending_order_number", c.order_number || "");
                } catch (e) {}
                window.location.href = payment_url;
                return;
              }
            } catch (e) {
              alert("Не удалось перейти к оплате: " + (e.message || "ошибка"));
            }
          }
        } else {
          const lines = conflicts
            .map(c => `• «${c.title}» — заказ №${c.order_number} (${c.order_status})`)
            .join("\n");
          alert(
            "Нельзя оформить: часть книг уже в активных заказах:\n\n" + lines +
            "\n\nОплатите существующий заказ в разделе «Покупки» или создайте копию книги для нового."
          );
        }
      } else if (err.status === 400 && err.data?.code === "books_incomplete") {
        const problems = err.data.problems || [];
        // Структурное повреждение (потерян разворот) — отдельный понятный текст с инструкцией.
        const structural = problems.find(p => p.code === "spreads_count_mismatch");
        if (structural) {
          alert(
            "Книга повреждена — часть страниц не сохранилась (обычно из-за обрыва связи).\n\n" +
            "Что сделать:\n" +
            "1. Обновите страницу конструктора.\n" +
            "2. Дождитесь полной загрузки всех разворотов.\n" +
            "3. Проверьте, что все страницы на месте и заполнены.\n" +
            "4. Оформите заказ заново.\n\n" +
            structural.message
          );
        } else {
          // Обычная незаполненность (пустые слоты / нет файла) — показываем детали.
          const lines = problems.slice(0, 10).map(p => `• ${p.message}`).join("\n");
          const extra = problems.length > 10 ? `\n…и ещё ${problems.length - 10}` : "";
          alert("Нельзя оформить — книга заполнена не полностью:\n\n" + lines + extra);
        }
      } else {
        alert("Ошибка при оформлении: " + err.message);
      }
      // Ключ детерминированный — повтор той же корзины НЕ создаст дубль.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCheckCode = async () => {
    if (!codeInput.trim()) return;
    setPromoFreeDelivery(false);
    setCodeStatus("checking");
    setCodeError("");
    setCodeData(null);
    setCodeType(null);
    setGiftMismatch(null);

    try {
      const bookFormatId = cartItems[0]?.bookFormatId ?? undefined;
      const result = await bookApi.checkCode({ code: codeInput.trim(), bookFormatId });

      setCodeType(result.type);

      if (!result.valid) {
        setCodeStatus("invalid");
        setCodeError(result.detail || "Код недействителен");
        return;
      }

      if (result.type === "promo") {
        if (!result.format_ok) {
          setCodeStatus("format_mismatch");
          setCodeError(result.format_message || "Промокод не подходит для этого формата");
          setCodeData(result);
          setPromoFreeDelivery(false);
        } else {
          setCodeStatus("valid");
          setCodeData(result);
          setPromoFreeDelivery(!!result.promo?.free_delivery);
        }
      }

      if (result.type === "gift") {
        const gift = result.gift;
        if (!gift || typeof gift !== "object") {
          setCodeStatus("invalid");
          setCodeError("Сервер не вернул данные сертификата. Повторите проверку.");
          return;
        }
        setCodeData(gift);

        // Одна позиция и один экземпляр.
        const totalCopies = cartItems.reduce(
          (sum, item) => sum + Number(item.quantity ?? 1), 0,
        );
        if (cartItems.length !== 1 || totalCopies !== 1) {
          setCodeStatus("mismatch");
          setGiftMismatch({
            message: `По сертификату можно оформить одну книгу в одном экземпляре. Сейчас экземпляров: ${totalCopies}.`,
            giftFormat: `${gift.book_format_title}`,
            problems: ["Сертификат действует только на одну книгу"],
          });
          return;
        }

        // Формат может отличаться; ограничение теперь по стоимости книги.
        const rawGiftValue = gift.gift_value;
        const giftValue = (
          (typeof rawGiftValue === "number" || typeof rawGiftValue === "string") &&
          String(rawGiftValue).trim() !== ""
        ) ? Number(rawGiftValue) : NaN;
        if (!Number.isFinite(giftValue) || giftValue <= 0) {
          setCodeStatus("invalid");
          setCodeData(null);
          setGiftMismatch(null);
          setCodeError(
            "У сертификата не указан номинал. Обратитесь в поддержку и сообщите код сертификата."
          );
          return;
        }
        if (Number(cartItems[0].price) < giftValue) {
          setCodeStatus("mismatch");
          setGiftMismatch({
            message: "Стоимость книги ниже номинала сертификата. Выберите книгу не дешевле сертификата.",
            giftFormat: `${gift.book_format_title} · ${gift.pages_count} стр.`,
            problems: [`Номинал книги: ${giftValue.toLocaleString("ru-RU")} ₽`],
          });
          return;
        }
        // Доплата за книгу и доставка считаются отдельно.
        setCodeStatus("valid");
      }
    } catch (err) {
      setCodeStatus("invalid");
      setCodeData(null);
      setGiftMismatch(null);
      const serverDetail = err?.data?.detail ?? err?.response?.data?.detail;
      const detail = Array.isArray(serverDetail) ? serverDetail.join(" ") : serverDetail;
      setCodeError(
        (typeof detail === "string" && detail) || err.message || "Ошибка проверки кода"
      );
    }
  };

  const upd = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const booksProblems = useCallback(() => {
    return getBooksProblems(cartItems);
  }, [cartItems])();

  const booksReady = booksProblems.length === 0;

  // ── Заголовок по шагу ─────────────────────────────────────────────────────
  const TITLES = { checkout: "Оформление заказа", success: "" };

  return (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          // onClose={onClose}
          sx={{ zIndex: 10013 }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: "blur(0px)" },
              animate: { opacity: 1, backdropFilter: "blur(8px)" },
              exit: { opacity: 0, backdropFilter: "blur(0px)" },
              transition: { duration: 0.3, ease: "easeInOut" },
            },
          }}
        >
          <ModalOverflow sx={{ height: "auto", p: 0 }}>
          <ModalDialog
            layout="center"
            component={motion.div}
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            sx={(theme) => ({
              p: 0,
              gap: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              [theme.breakpoints.up("sm")]: {
                width: 500,
                maxHeight: "90vh !important",
                borderRadius: "20px",
              },
              [theme.breakpoints.down("sm")]: {
                width: "100vw",
                minWidth: "100vw",
                minHeight: "100vh",
                m: 0,
                borderRadius: 0,
              },
            })}
          >
            {/* ── Шапка ── */}
            {step !== "success" && (
              <Box sx={{
                px: 2.5, py: 2,
                borderBottom: "1px solid", borderColor: "divider",
                flexShrink: 0,
                display: "flex", alignItems: "center", gap: 1,
              }}>
                {step === "checkout" && (
                  <ShoppingCartRoundedIcon sx={{ fontSize: 18, color: "primary.400" }} />
                )}

                <Typography level="title-md" fontWeight={700} sx={{ flex: 1 }}>
                  {TITLES[step]}
                  {step === "cart" && cartItems.length > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 20, height: 20,
                        borderRadius: "50%",
                        bgcolor: "primary.500",
                        fontSize: "11px", color: "white", fontWeight: 700, lineHeight: 1,
                        verticalAlign: "middle",
                      }}
                    >
                      {cartItems.length}
                    </Box>
                  )}
                </Typography>

                <ModalClose sx={{ position: "static" }} onClick={onClose} />
              </Box>
            )}

            {/* ── Контент с анимацией между шагами ── */}
            <Box sx={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
              <AnimatePresence mode="wait" initial={false} custom={direction}>
                <motion.div
                  key={step}
                  custom={direction}
                  variants={slideVariants}
                  initial={direction > 0 ? "enterFromRight" : "enterFromLeft"}
                  animate="center"
                  exit={direction > 0 ? "exitToLeft" : "exitToRight"}
                  transition={{ type: "spring", stiffness: 380, damping: 36, mass: 0.8 }}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "hidden",
                    overflowX: "hidden",
                    minHeight: 0,
                  }}
                >
                  {/* ══════════ ШАГ 2: ОФОРМЛЕНИЕ ══════════ */}
                  {step === "checkout" && (
                    <>
                      <Box ref={scrollBoxRef} sx={{ flex: 1, px: 2.5, py: 2, overflowY: "auto" }}>

                        {/* ── Список книг в заказе ── */}
                        {cartItems.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography level="body-xs" sx={{
                              fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.08em", color: "text.tertiary",
                              mb: 1, fontSize: "10px",
                            }}>
                              Книги в заказе
                            </Typography>
                            <Stack gap={1}>
                              {cartItems.map((item, idx) => (
                                <CartItemCard
                                  key={idx}
                                  item={item}
                                  onRemove={() => {
                                    onRemoveItem(idx);
                                    if (cartItems.length === 1) {
                                      onClose();
                                    }
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                        <Divider sx={{ mb: 1 }} />

                        <Stack gap={2}>

                          {/* — Данные клиента — */}
                          <Box>
                            <Typography level="body-xs" sx={{
                              fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.08em", color: "text.tertiary",
                              mb: 1.25, fontSize: "10px",
                            }}>
                              Данные получателя
                            </Typography>
                            <Stack gap={1.25}>
                              <FormField
                                label="Имя"
                                required
                                icon={<PersonRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                                placeholder="Иван"
                                value={form.name}
                                onChange={e => upd("name", sanitizeName(e.target.value))}
                                error={errors.name}
                              />
                              <FormField
                                label="Фамилия"
                                required
                                icon={<PersonRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                                placeholder="Иванов"
                                value={form.last_name}
                                onChange={e => upd("last_name", sanitizeName(e.target.value))}
                                error={errors.last_name}
                              />
                              <FormField
                                label="Телефон"
                                required
                                icon={<PhoneRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                                placeholder="+7 (___) ___-__-__"
                                value={form.phone}
                                onChange={e => upd("phone", e.target.value)}
                                error={errors.phone}
                                slotProps={{
                                  input: {
                                    component: PhoneMaskInput,
                                    name: 'phone',
                                  },
                                }}
                              />

                              {/* Скидка по телефону */}
                              {phoneDiscountLoading && (
                                <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
                                  <CircularProgress size="sm" sx={{ "--CircularProgress-size": "14px" }} />
                                  <Typography level="body-xs" color="neutral">
                                    Проверяем скидки...
                                  </Typography>
                                </Stack>
                              )}
                              {!phoneDiscountLoading && isPhoneDiscountActive && phoneDiscount && (
                                <Box sx={{
                                  mt: 0.75, p: 1.25, borderRadius: "10px",
                                  bgcolor: "color-mix(in srgb, var(--joy-palette-success-100) 50%, transparent)",
                                  border: "1px solid", borderColor: "success.200",
                                }}>
                                  <Stack direction="row" alignItems="center" gap={0.5}>
                                    <PercentRoundedIcon sx={{ fontSize: 14, color: "success.600" }} />
                                    <Typography level="body-xs" sx={{ color: "success.700", fontWeight: 700 }}>
                                      Скидка {parseFloat(phoneDiscount.percent)}% применена!
                                    </Typography>
                                  </Stack>
                                  {phoneDiscount.title && (
                                    <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                      {phoneDiscount.title}
                                    </Typography>
                                  )}
                                  {phoneDiscount.description && (
                                    <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25, fontStyle: "italic" }}>
                                      {phoneDiscount.description}
                                    </Typography>
                                  )}
                                  {phoneDiscount.book_format_title && (
                                    <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                      Формат: {phoneDiscount.book_format_title}
                                    </Typography>
                                  )}
                                  <Typography level="body-xs" sx={{ color: "neutral.500", mt: 0.5, fontSize: "10px" }}>
                                    Не суммируется с промокодами
                                  </Typography>
                                </Box>
                              )}
                              {!phoneDiscountLoading && phoneDiscount && promoApplied && (
                                <Typography level="body-xs" sx={{ color: "warning.600", mt: 0.5, fontSize: "11px" }}>
                                  Скидка {parseFloat(phoneDiscount.percent)}% по телефону не применяется — используется промокод
                                </Typography>
                              )}
                              <FormField
                                label="Email"
                                required
                                icon={<EmailRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                                placeholder="mail@example.com"
                                value={form.email}
                                onChange={e => upd("email", e.target.value)}
                                error={errors.email}
                              />
                              <Box>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography level="body-xs" sx={{ fontWeight: 600, color: "text.secondary" }}>
                                    Дата рождения <Typography component="span" level="body-xs" color="neutral">(необязательно)</Typography>
                                  </Typography>
                                  {(birthDay || birthMonth || birthYear) && (
                                    <IconButton
                                      size="sm"
                                      variant="soft"
                                      color="neutral"
                                      onClick={() => {
                                        setBirthDay("");
                                        setBirthMonth("");
                                        setBirthYear("");
                                        upd("birthDate", "");
                                      }}
                                      sx={{ "--IconButton-size": "24px", borderRadius: "6px" }}
                                    >
                                      <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  )}
                                </Stack>
                                <Stack direction="row" gap={1}>
                                  <Select
                                    size="sm"
                                    placeholder="День"
                                    value={birthDay || null}
                                    onChange={(_, val) => {
                                      setBirthDay(val);
                                      handleBirthChange(val, birthMonth, birthYear);
                                    }}
                                    sx={{ flex: 1, borderRadius: 0, p: 2, fontSize: 15 }}
                                    slotProps={{ listbox: { sx: { zIndex: 100000 } } }}
                                  >
                                    {DAYS.map(d => (
                                      <Option key={d} value={d}>{d}</Option>
                                    ))}
                                  </Select>

                                  <Select
                                    size="sm"
                                    placeholder="Месяц"
                                    value={birthMonth || null}
                                    onChange={(_, val) => {
                                      setBirthMonth(val);
                                      handleBirthChange(birthDay, val, birthYear);
                                    }}
                                    sx={{ flex: 2, borderRadius: 0, p: 2, fontSize: 15 }}
                                    slotProps={{ listbox: { sx: { zIndex: 100000 } } }}
                                  >
                                    {MONTHS.map((m, i) => (
                                      <Option key={i + 1} value={i + 1}>{m}</Option>
                                    ))}
                                  </Select>

                                  <Select
                                    size="sm"
                                    placeholder="Год"
                                    value={birthYear || null}
                                    onChange={(_, val) => {
                                      setBirthYear(val);
                                      handleBirthChange(birthDay, birthMonth, val);
                                    }}
                                    sx={{ flex: 2, borderRadius: 0, p: 2, fontSize: 15 }}
                                    slotProps={{ listbox: { sx: { zIndex: 100000 } } }}
                                  >
                                    {YEARS.map(y => (
                                      <Option key={y} value={y}>{y}</Option>
                                    ))}
                                  </Select>
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>

                          {/* — Промокод / Сертификат — */}
                          <Box sx={{
                            p: 2,
                            borderRadius: "14px",
                            border: "1.5px solid transparent",
                            backgroundImage: `
                              linear-gradient(var(--joy-palette-background-surface), var(--joy-palette-background-surface)),
                              linear-gradient(120deg, #f271b8, #eecf52, #ee6b6b, #ec74b6)
                            `,
                            backgroundOrigin: "border-box",
                            backgroundClip: "padding-box, border-box",
                            position: "relative",
                            overflow: "hidden",
                            transition: "border-color 0.2s",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              inset: 0,
                              borderRadius: "14px",
                              background: "linear-gradient(120deg, rgba(249,168,212,0.12), rgba(253,230,138,0.12), rgba(252,165,165,0.12))",
                              backgroundSize: "200% 200%",
                              animation: "promoShimmer 4s ease infinite",
                              pointerEvents: "none",
                              zIndex: 0,
                            },
                          }}>
                            <style>{`
                              @keyframes promoShimmer {
                                0%   { background-position: 0% 50%; }
                                50%  { background-position: 100% 50%; }
                                100% { background-position: 0% 50%; }
                              }
                            `}</style>
                            <Stack direction="row" alignItems="center" gap={0.75} mb={1} sx={{ position: "relative", zIndex: 1 }}>
                              <DiscountRoundedIcon sx={{ fontSize: 16, color: "primary.400" }} />
                              <Typography level="body-xs" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                Промокод / Код сертификата
                              </Typography>
                            </Stack>

                            <Stack direction="row" gap={1} sx={{ position: "relative", zIndex: 1 }}>
                              <Input
                                size="sm"
                                placeholder="Введите промокод или код сертификата"
                                value={codeInput}
                                onChange={e => {
                                  setCodeInput(e.target.value.toUpperCase());
                                  if (codeStatus) {
                                    setCodeStatus(null);
                                    setCodeError("");
                                    setPromoFreeDelivery(false);
                                    setCodeData(null);
                                    setCodeType(null);
                                    setGiftMismatch(null);
                                  }
                                }}
                                disabled={codeStatus === "valid"}
                                color={
                                  codeStatus === "valid"            ? "success" :
                                  codeStatus === "invalid"          ? "danger" :
                                  codeStatus === "mismatch"         ? "warning" :
                                  codeStatus === "format_mismatch"  ? "warning" :
                                                                      "neutral"
                                }
                                sx={{
                                  flex: 1,
                                  borderRadius: 0,
                                  p: 2, fontSize: 15,
                                  fontFamily: "monospace", fontWeight: 700,
                                  letterSpacing: "0.06em", fontSize: "14px",
                                }}
                              />
                              {codeStatus === "valid" ? (
                                <Button
                                  size="sm" variant="soft" color="neutral"
                                  onClick={() => {
                                    setCodeInput("");
                                    setCodeStatus(null);
                                    setCodeError("");
                                    setPromoFreeDelivery(false);
                                    setCodeData(null);
                                    setCodeType(null);
                                    setGiftMismatch(null);
                                  }}
                                  sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "12px", px: 2 }}
                                >
                                  Отменить
                                </Button>
                              ) : (
                                <Button
                                  size="sm" variant="soft"
                                  loading={codeStatus === "checking"}
                                  disabled={!codeInput.trim() || codeStatus === "valid"}
                                  onClick={handleCheckCode}
                                  sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "12px", px: 2 }}
                                >
                                  Проверить
                                </Button>
                              )}
                            </Stack>

                            {/* Ошибка */}
                            {(codeStatus === "invalid" || codeStatus === "format_mismatch") && codeError && (
                              <Typography level="body-xs" sx={{
                                color: codeStatus === "invalid" ? "danger.500" : "warning.700",
                                mt: 0.75, fontWeight: 500,
                              }}>
                                {codeError}
                              </Typography>
                            )}

                            {/* Успех — промокод */}
                            {codeStatus === "valid" && codeType === "promo" && codeData && (
                              <Box sx={{
                                mt: 1, p: 1.5, borderRadius: "10px",
                                bgcolor: "color-mix(in srgb, var(--joy-palette-success-100) 50%, transparent)",
                                border: "1px solid", borderColor: "success.200",
                              }}>
                                <Typography level="body-xs" sx={{ color: "success.700", fontWeight: 700 }}>
                                  {codeData.promo?.discount_type === "fixed"
                                    ? `Промокод применён! Скидка ${Math.round(parseFloat(codeData.promo?.amount || 0)).toLocaleString("ru-RU")} ₽`
                                    : `Промокод применён! Скидка ${codeData.promo?.percent}%`}
                                </Typography>
                                {codeData.promo?.book_format_title && (
                                  <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                    Действует для формата: {codeData.promo.book_format_title}
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Успех — сертификат */}
                            {codeStatus === "valid" && codeType === "gift" && codeData && (
                              <Box sx={{
                                mt: 1, p: 1.5, borderRadius: "10px",
                                bgcolor: "color-mix(in srgb, var(--joy-palette-success-100) 50%, transparent)",
                                border: "1px solid", borderColor: "success.200",
                              }}>
                                <Typography level="body-xs" sx={{ color: "success.700", fontWeight: 700 }}>
                                  ✓ Сертификат применён к расчёту
                                </Typography>
                                <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                  Формат: {codeData.book_format_title} · {codeData.pages_count} стр.
                                </Typography>
                                <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                  Можно выбрать другой формат и количество страниц, если книга
                                  стоит не меньше номинала. Более дорогая книга оформляется с доплатой.
                                </Typography>
                                {giftDiscount > 0 && (
                                  <Typography level="body-xs" sx={{ color: "success.700", mt: 0.25 }}>
                                    Сертификат покроет {Math.round(giftDiscount).toLocaleString("ru-RU")} ₽
                                  </Typography>
                                )}
                                {codeData.delivery_paid && (
                                  <Typography level="body-xs" sx={{ color: "success.700", fontWeight: 700, mt: 0.25 }}>
                                    Бюджет доставки: {Number(codeData.delivery_value || 0).toLocaleString("ru-RU")} ₽.
                                    Доплата: {totalDelivery.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽.
                                  </Typography>
                                )}
                                {codeData.sender_name && (
                                  <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                    От: {codeData.sender_name}
                                  </Typography>
                                )}
                                {codeData.wishes && (
                                  <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.25, fontStyle: "italic" }}>
                                    «{codeData.wishes}»
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Предупреждение — несовпадение формата сертификата */}
                            {codeStatus === "mismatch" && giftMismatch && (
                              <Box sx={{
                                mt: 1, p: 1.5, borderRadius: "10px",
                                bgcolor: "color-mix(in srgb, var(--joy-palette-warning-100) 50%, transparent)",
                                border: "1px solid", borderColor: "warning.300",
                              }}>
                                <Typography level="body-xs" sx={{ color: "warning.700", fontWeight: 700 }}>
                                  ⚠ {giftMismatch.message}
                                </Typography>
                                <Typography level="body-xs" sx={{ color: "warning.600", mt: 0.5 }}>
                                  Сертификат: {giftMismatch.giftFormat}
                                </Typography>
                                {giftMismatch.problems.map((p, i) => (
                                  <Typography key={i} level="body-xs" sx={{ color: "warning.700", mt: 0.25 }}>
                                    · {p}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>

                          {/* — Автоскидка (если нет промокода) — */}
                          {!promoPercent && autoDiscount && (
                            <Box sx={{
                              p: 2, borderRadius: "14px",
                              bgcolor: "color-mix(in srgb, var(--joy-palette-success-50) 70%, transparent)",
                              border: "1.5px solid", borderColor: "success.200",
                            }}>
                              <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
                                <PercentRoundedIcon sx={{ fontSize: 16, color: "success.500" }} />
                                <Typography level="body-xs" sx={{ fontWeight: 700, color: "success.700" }}>
                                  Скидка применена автоматически
                                </Typography>
                              </Stack>
                              <Typography level="body-xs" sx={{ color: "success.600" }}>
                                {autoDiscount.title} — {autoDiscount.percent}%
                              </Typography>
                              {autoDiscount.description && (
                                <Typography level="body-xs" sx={{ color: "success.600", mt: 0.25 }}>
                                  {autoDiscount.description}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* — Доставка — */}
                          <Box>
                            <Typography level="body-xs" sx={{
                              fontWeight: 700, textTransform: "uppercase",
                              letterSpacing: "0.08em", color: "text.tertiary",
                              mb: 1.25, fontSize: "10px",
                            }}>
                              Способ доставки
                            </Typography>
                            <Stack gap={1}>
                              {DELIVERY_TYPES.map(type => (
                                <DeliveryCard
                                  key={type.id}
                                  type={type}
                                  selected={form.deliveryType === type.id}
                                  onClick={id => upd("deliveryType", id)}
                                  deliveryPrice={promoFreeDelivery ? 0 : (deliveryPrice == null ? null : totalDelivery)}
                                  loadingPrice={loadingPrice}
                                />
                              ))}
                            </Stack>
                          </Box>


                          {/* — СДЭК: поля адреса — */}
                          <AnimatePresence>
                            {form.deliveryType === "cdek" && (
                              <>
                                <Divider />
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.25, ease: "easeInOut" }}
                                  style={{ overflow: "visible" }}
                                >
                                  <Stack gap={1.25} sx={{ pt: 0.5, overflow: "visible" }}>

                                    {/* Поиск города */}
                                    <Box>
                                      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600, color: errors.city ? "danger.500" : "text.secondary" }}>
                                        Город доставки<Box component="span" sx={{ color: "danger.400", ml: 0.25 }}>*</Box>
                                      </Typography>
                                      <Box sx={{ position: "relative" }}>
                                        <Input
                                          size="sm"
                                          startDecorator={
                                            cityLoading
                                              ? <CircularProgress size="sm" sx={{ "--CircularProgress-size": "14px" }} />
                                              : <SearchRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />
                                          }
                                          placeholder="Начните вводить город..."
                                          value={form.city}
                                          onChange={e => handleCityInput(e.target.value)}
                                          error={!!errors.city}
                                          sx={{ borderRadius: 0, p: 2, fontSize: 15 }}
                                        />
                                        <AnimatePresence>
                                          {showCityDropdown && cityResults.length > 0 && (
                                            <motion.div
                                              initial={{ opacity: 0, y: 4 }}
                                              animate={{ opacity: 1, y: 0 }}
                                              exit={{ opacity: 0, y: 4 }}
                                              transition={{ duration: 0.15 }}
                                              style={{
                                                position: "absolute", bottom: "calc(100% + 4px)",
                                                left: 0, right: 0, zIndex: 100,
                                                background: "var(--joy-palette-background-surface)",
                                                borderRadius: "10px",
                                                border: "1px solid var(--joy-palette-divider)",
                                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
                                                maxHeight: 220, overflowY: "auto",
                                              }}
                                            >
                                              {cityResults.map((city, i) => (
                                                <Box key={i} onClick={() => selectCity(city)} sx={{
                                                  px: 1.5, py: 1, cursor: "pointer", fontSize: "13px",
                                                  borderBottom: i < cityResults.length - 1 ? "1px solid" : "none",
                                                  borderColor: "divider",
                                                  "&:hover": { bgcolor: "background.level1" },
                                                  transition: "background 0.12s",
                                                }}>
                                                  <Typography level="body-sm" fontWeight={500}>{city.city}</Typography>
                                                  <Typography level="body-xs" color="neutral">{city.region}, {city.country}</Typography>
                                                </Box>
                                              ))}
                                            </motion.div>
                                          )}
                                        </AnimatePresence>
                                      </Box>
                                      {errors.city && (
                                        <Typography level="body-xs" sx={{ color: "danger.500", mt: 0.25 }}>{errors.city}</Typography>
                                      )}
                                    </Box>

                                    {/* Пункт выдачи — карта Leaflet + список */}
                                    {form.cityCode && (
                                      <Box>
                                        <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600, color: errors.pvz ? "danger.500" : "text.secondary" }}>
                                          Пункт выдачи<Box component="span" sx={{ color: "danger.400", ml: 0.25 }}>*</Box>
                                        </Typography>

                                        {pvzLoading ? (
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                                            <CircularProgress size="sm" sx={{ "--CircularProgress-size": "16px" }} />
                                            <Typography level="body-xs" color="neutral">Загружаем пункты выдачи...</Typography>
                                          </Box>
                                        ) : pvzList.length > 0 ? (
                                          <>
                                            {/* Карта */}
                                            <SdekMap
                                              pvzList={pvzFiltered.length > 0 ? pvzFiltered : pvzList}
                                              selectedCode={form.pvzCode}
                                              onSelect={(pvz) => {
                                                upd("pvzCode", pvz.code);
                                                upd("pvzAddress", pvz.address_full || pvz.address);
                                                calcSdekPrice(form.cityCode);
                                              }}
                                            />

                                            {/* Поиск по ПВЗ */}
                                            <Box sx={{ position: "relative", mt: 1 }}>
                                              <Input
                                                size="sm"
                                                startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />}
                                                endDecorator={
                                                  pvzSearchQuery ? (
                                                    <Box
                                                      onClick={() => { setPvzSearchQuery(""); setPvzFiltered([]); }}
                                                      sx={{ cursor: "pointer", display: "flex", alignItems: "center", color: "text.tertiary", "&:hover": { color: "text.primary" } }}
                                                    >
                                                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                                    </Box>
                                                  ) : null
                                                }
                                                placeholder="Поиск по адресу или названию ПВЗ..."
                                                value={pvzSearchQuery}
                                                onChange={e => handlePvzSearch(e.target.value)}
                                                sx={{ borderRadius: 0, p: 2, fontSize: 15 }}
                                              />
                                              <AnimatePresence>
                                                {pvzSearchQuery && pvzFiltered.length > 0 && (
                                                  <motion.div
                                                    initial={{ opacity: 0, y: 4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 4 }}
                                                    transition={{ duration: 0.15 }}
                                                    style={{
                                                      position: "absolute", bottom: "calc(100% + 4px)",
                                                      left: 0, right: 0, zIndex: 1000,
                                                      background: "var(--joy-palette-background-surface)",
                                                      borderRadius: "10px",
                                                      border: "1px solid var(--joy-palette-divider)",
                                                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                                      overflow: "hidden",
                                                      maxHeight: 220,
                                                      overflowY: "auto",
                                                    }}
                                                  >
                                                    {pvzFiltered.map((pvz, i) => (
                                                      <Box
                                                        key={pvz.code}
                                                        onClick={() => {
                                                          upd("pvzCode", pvz.code);
                                                          upd("pvzAddress", pvz.address_full || pvz.address);
                                                          calcSdekPrice(form.cityCode);
                                                          setPvzSearchQuery("");
                                                          setPvzFiltered([]);
                                                          setTimeout(() => {
                                                            scrollBoxRef.current?.scrollTo({ top: scrollBoxRef.current.scrollHeight, behavior: "smooth" });
                                                          }, 200);
                                                        }}
                                                        sx={{
                                                          px: 1.5, py: 1, cursor: "pointer",
                                                          borderBottom: i < pvzFiltered.length - 1 ? "1px solid" : "none",
                                                          borderColor: "divider",
                                                          "&:hover": { bgcolor: "background.level1" },
                                                          transition: "background 0.12s",
                                                        }}
                                                      >
                                                        <Typography level="body-sm" fontWeight={500}>
                                                          {pvz.name}
                                                        </Typography>
                                                        <Typography level="body-xs" color="neutral">
                                                          {pvz.address_full || pvz.address}
                                                          {pvz.work_time && ` · ${pvz.work_time}`}
                                                        </Typography>
                                                      </Box>
                                                    ))}
                                                  </motion.div>
                                                )}
                                                {pvzSearchQuery && pvzFiltered.length === 0 && (
                                                  <motion.div
                                                    initial={{ opacity: 0, y: 4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 4 }}
                                                    transition={{ duration: 0.15 }}
                                                    style={{
                                                      position: "absolute", bottom: "calc(100% + 4px)",
                                                      left: 0, right: 0, zIndex: 1000,
                                                      background: "var(--joy-palette-background-surface)",
                                                      borderRadius: "10px",
                                                      border: "1px solid var(--joy-palette-divider)",
                                                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                                      padding: "10px 14px",
                                                    }}
                                                  >
                                                    <Typography level="body-xs" color="neutral">Ничего не найдено</Typography>
                                                  </motion.div>
                                                )}
                                              </AnimatePresence>
                                            </Box>

                                            {/* Выбранный ПВЗ */}
                                            {form.pvzCode && (
                                              <Box sx={{
                                                mt: 1, p: 1.5, borderRadius: "10px",
                                                bgcolor: "background.level1",
                                                border: "1px solid", borderColor: "divider",
                                              }}>
                                                <Typography level="body-xs" fontWeight={700} sx={{ color: "success.600" }}>
                                                  ✓ {form.pvzAddress}
                                                </Typography>
                                              </Box>
                                            )}
                                          </>
                                        ) : (
                                          <Typography level="body-xs" color="neutral">Нет пунктов выдачи в этом городе</Typography>
                                        )}

                                        {errors.pvz && (
                                          <Typography level="body-xs" sx={{ color: "danger.500", mt: 0.25 }}>{errors.pvz}</Typography>
                                        )}
                                      </Box>
                                    )}

                                    {/* Комментарий */}
                                    {/* <Box>
                                      <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 600, color: "text.secondary" }}>
                                        Комментарий к доставке
                                      </Typography>
                                      <Input
                                        size="sm"
                                        placeholder="Комментарий"
                                        value={form.comment}
                                        onChange={e => upd("comment", e.target.value)}
                                        sx={{ borderRadius: 0, px: 2, py: 1 }}
                                      />
                                    </Box> */}

                                  </Stack>
                                </motion.div>
                              </>
                            )}
                          </AnimatePresence>

                        </Stack>
                      </Box>

                      {/* — Итог и кнопка — */}
                      <Box sx={{
                        px: 2.5, py: 2,
                        borderTop: "1px solid", borderColor: "divider",
                        flexShrink: 0,
                        position: "sticky", bottom: 0,
                        bgcolor: "background.surface",
                      }}>
                        <Stack gap={0.5} mb={1.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography level="body-xs" color="neutral">Книги:</Typography>
                            <Typography level="body-xs" fontWeight={600}>
                              {totalItems.toLocaleString("ru-RU")} ₽
                            </Typography>
                          </Stack>

                          {codeStatus === "valid" && codeType === "gift" && giftDiscount > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography level="body-xs" sx={{ color: "success.600" }}>
                                Подарок (скидка):
                              </Typography>
                              <Typography level="body-xs" sx={{ color: "success.600", fontWeight: 600 }}>
                                −{Math.round(giftDiscount).toLocaleString("ru-RU")} ₽
                              </Typography>
                            </Stack>
                          )}

                          {discountAmount > 0 && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography level="body-xs" sx={{ color: "success.600" }}>
                                {promoIsFixed
                                  ? `Промокод (−${Math.round(promoFixedAmount).toLocaleString("ru-RU")} ₽):`
                                  : promoPercent
                                    ? `Промокод (−${promoPercent}%):`
                                    : phonePercent
                                      ? `Скидка по телефону (−${phonePercent}%):`
                                      : `Скидка (−${autoPercent}%):`}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: "success.600", fontWeight: 600 }}>
                                −{discountAmount.toLocaleString("ru-RU")} ₽
                              </Typography>
                            </Stack>
                          )}

                          <Stack direction="row" justifyContent="space-between">
                            <Typography level="body-xs" color="neutral">Доставка:</Typography>
                            <Typography level="body-xs" fontWeight={600} sx={(promoFreeDelivery || giftFreeDelivery) ? { color: "success.600" } : {}}>
                              {(promoFreeDelivery || giftFreeDelivery)
                                ? "Бесплатно"
                                : deliveryPrice != null
                                  ? `${totalDelivery.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`
                                  : "уточняется"}
                            </Typography>
                          </Stack>
                          {giftApplied && (
                            <Typography level="body-xs" sx={{ color: "success.700" }}>
                              Доплата за книгу: {Math.max(0, totalItems - giftDiscount).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽.
                              Доплата за доставку: {totalDelivery.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽.
                              {!giftCartValid && " Выберите одну книгу стоимостью не ниже номинала сертификата."}
                            </Typography>
                          )}
                          {deliveryError && !promoFreeDelivery && (
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                              <Typography level="body-xs" sx={{ color: "danger.500", flex: 1, mr: 1 }}>
                                {deliveryError}
                              </Typography>
                              <Button
                                size="sm" variant="soft" color="danger"
                                loading={loadingPrice}
                                onClick={() => form.cityCode && calcSdekPrice(form.cityCode)}
                                sx={{ borderRadius: "8px", minHeight: 0, py: 0.25 }}
                              >
                                Повторить
                              </Button>
                            </Stack>
                          )}
                          <Divider sx={{ my: 0.25 }} />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography level="body-sm" fontWeight={700}>Итого:</Typography>
                            <Typography level="title-md" fontWeight={800} sx={{ color: "primary.600" }}>
                              {(deliveryPrice != null || promoFreeDelivery || giftFreeDelivery)
                                ? `${Math.max(0, Number(totalFinal) - giftDiscount - discountAmount).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`
                                : `от ${Math.max(0, Number(totalItems) - giftDiscount - discountAmount).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Галочка 1: Политика ПД */}
                        <Stack direction="row" sx={{ mb: 1, gap: 1, alignItems: "flex-start" }}>
                          <Checkbox
                            size="sm"
                            checked={agreedMarketing}
                            onChange={(e) => {
                              setAgreedMarketing(e.target.checked);
                              if (e.target.checked && errors.agreedMarketing)
                                setErrors(prev => { const { agreedMarketing, ...r } = prev; return r; });
                            }}
                            sx={{ alignItems: "flex-start" }}
                          />
                          <Typography level="body-xs" sx={{ lineHeight: 1.4 }}>
                            Я согласен с{" "}
                            <Link href="https://photobook.magicboard.shop/policy" target="_blank" rel="noopener noreferrer"
                              sx={{ fontSize: "inherit", fontWeight: 600, color: "warning.500" }} onClick={e => e.stopPropagation()}>
                              политикой конфиденциальности
                            </Link>
                            {" "}и{" "}
                            <Link href="http://photobook.magicboard.shop/oferta" target="_blank" rel="noopener noreferrer"
                              sx={{ fontSize: "inherit", fontWeight: 600, color: "warning.500" }} onClick={e => e.stopPropagation()}>
                              договором оферты
                            </Link>{" "}*
                          </Typography>
                        </Stack>
                        {errors.agreedMarketing && (
                          <Typography level="body-xs" sx={{ color: "danger.500", mb: 1, ml: 3.5, fontSize: "11px" }}>
                            {errors.agreedMarketing}
                          </Typography>
                        )}

                        {/* Галочка 2: Передача ПД */}
                        <Stack direction="row" sx={{ mb: 1, gap: 1, alignItems: "flex-start" }}>
                          <Checkbox
                            size="sm"
                            checked={agreedThirdParty}
                            onChange={(e) => {
                              setAgreedThirdParty(e.target.checked);
                              if (e.target.checked && errors.agreedThirdParty)
                                setErrors(prev => { const { agreedThirdParty, ...r } = prev; return r; });
                            }}
                            sx={{ alignItems: "flex-start" }}
                          />
                          <Typography level="body-xs" sx={{ lineHeight: 1.4 }}>
                            Я согласен на{" "}
                            <Link href="http://photobook.magicboard.shop/persdannpered" target="_blank" rel="noopener noreferrer"
                              sx={{ fontSize: "inherit", fontWeight: 600, color: "warning.500" }} onClick={e => e.stopPropagation()}>
                              передачу персональных данных
                            </Link>{" "}*
                          </Typography>
                        </Stack>
                        {errors.agreedThirdParty && (
                          <Typography level="body-xs" sx={{ color: "danger.500", mb: 1, ml: 3.5, fontSize: "11px" }}>
                            {errors.agreedThirdParty}
                          </Typography>
                        )}

                        {/* Галочка 3: Обработка */}
                        <Stack direction="row" sx={{ mb: 1.5, gap: 1, alignItems: "flex-start" }}>
                          <Checkbox
                            size="sm"
                            checked={agreed}
                            onChange={(e) => {
                              setAgreed(e.target.checked);
                              if (e.target.checked && errors.agreed)
                                setErrors(prev => { const { agreed, ...r } = prev; return r; });
                            }}
                            sx={{ alignItems: "flex-start" }}
                          />
                          <Typography level="body-xs" sx={{ lineHeight: 1.4 }}>
                            Я согласен на{" "}
                            <Link href="http://photobook.magicboard.shop/pesdannsogl" target="_blank" rel="noopener noreferrer"
                              sx={{ fontSize: "inherit", fontWeight: 600, color: "warning.500" }} onClick={e => e.stopPropagation()}>
                              обработку персональных данных
                            </Link>{" "}*
                          </Typography>
                        </Stack>
                        {errors.agreed && (
                          <Typography level="body-xs" sx={{ color: "danger.500", mb: 1, ml: 3.5, fontSize: "11px" }}>
                            {errors.agreed}
                          </Typography>
                        )}

                        <Button
                          fullWidth size="lg"
                          loading={submitting}
                          disabled={submitting || !booksReady || !agreed || !agreedThirdParty || !agreedMarketing || deliveryNeedsPrice || itemsNeedPrice || !giftCartValid}
                          startDecorator={<CheckCircleRoundedIcon />}
                          onClick={handleSubmit}
                          sx={{ borderRadius: "12px", fontWeight: 700 }}
                        >
                          Оформить заказ
                        </Button>
                        {!booksReady && booksProblems.length > 0 && (
                          <Typography level="body-xs" sx={{ color: "danger.500", mt: 1, textAlign: "center", fontSize: "11px" }}>
                            {booksProblems[0]}{booksProblems.length > 1 ? ` (+ещё ${booksProblems.length - 1})` : ""}
                          </Typography>
                        )}
                        {itemsNeedPrice && (
                          <Typography level="body-xs" sx={{ color: "danger.500", mt: 1, textAlign: "center", fontSize: "11px" }}>
                            Стоимость книги не рассчитана. Обновите страницу корзины и попробуйте снова.
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}

                  {/* ══════════ ШАГ 3: УСПЕХ ══════════ */}
                  {step === "success" && (
                    <SuccessScreen onClose={onClose} />
                  )}

                </motion.div>
              </AnimatePresence>
            </Box>
          </ModalDialog>
        </ModalOverflow>
      </Modal>
    )}
    </AnimatePresence>
  );
}